/**
 * Shared result sorting utilities for race results display
 */

/**
 * Compare two results by time, handling null values and status
 * @param {number|null} aTime - Time for result A
 * @param {number|null} bTime - Time for result B
 * @param {Object} a - Result object A
 * @param {Object} b - Result object B
 * @param {boolean} forceOK - Whether to require OK status for sorting
 * @returns {number|null} Sort comparison result, or null if fallback sorting needed
 */
export function compareByTime(aTime, bTime, a, b, forceOK = true) {
	// If both have times and meet status requirements, compare them
	if (aTime != null && bTime != null && ((a.status === "OK" && b.status === "OK") || !forceOK)) {
		return aTime > bTime ? 1 : aTime < bTime ? -1 : (a.id > b.id ? 1 : -1);
	}
	// If only A has time and meets status, A is higher
	if (aTime != null && (a.status === "OK" || !forceOK)) {
		return -1;
	}
	// If only B has time and meets status, B is higher
	if (bTime != null && (b.status === "OK" || !forceOK)) {
		return 1;
	}
	// Neither has valid time - need fallback sorting
	return null;
}

/**
 * Calculate split statistics for a result (only considers public timing points)
 * @param {Array} splits - Array of split objects
 * @returns {Object} Object with numSplits and lastSplitIndex
 */
export function calculateSplitStats(splits) {
	let numSplits = 0;
	let lastSplitIndex = 0;

	if (splits != undefined) {
		splits.forEach(function(split, index) {
			// Only consider public timing points for sorting
			if (split.elapsed != null && split.isPublic !== false) {
				numSplits++;
				if (index > lastSplitIndex) {
					lastSplitIndex = index;
				}
			}
		});
	}

	return { numSplits, lastSplitIndex };
}

/**
 * Compare two results by their splits when no total time is available
 * @param {Object} a - Result object A
 * @param {Object} b - Result object B
 * @param {number} [timeBonusA=0] - Time bonus/penalty to add to A's elapsed times
 * @param {number} [timeBonusB=0] - Time bonus/penalty to add to B's elapsed times
 * @returns {number} Sort comparison result
 */
export function compareBySplits(a, b, timeBonusA = 0, timeBonusB = 0) {
	const aSplits = a.splits;
	const bSplits = b.splits;

	const aStats = calculateSplitStats(aSplits);
	const bStats = calculateSplitStats(bSplits);

	// If neither has splits, return 0 (caller should handle fallback)
	if (aStats.numSplits === 0 && bStats.numSplits === 0) {
		return 0;
	}

	// More splits = further in race = higher ranking
	if (aStats.numSplits !== bStats.numSplits) {
		return aStats.numSplits > bStats.numSplits ? -1 : 1;
	}

	// Same number of splits - compare elapsed times at last split
	const aElapsed = aSplits[aStats.lastSplitIndex].elapsed + timeBonusA;
	const bElapsed = bSplits[bStats.lastSplitIndex].elapsed + timeBonusB;

	return aElapsed > bElapsed ? 1 : aElapsed < bElapsed ? -1 : 0;
}

/**
 * Standard results sort function for normal races
 * @param {Object} a - Result object A
 * @param {Object} b - Result object B
 * @param {Object} options - Sort options
 * @param {string|null} options.splitName - If set, sort by this split instead of total time
 * @returns {number} Sort comparison result
 */
export function resultsSortFn(a, b, options = {}) {
	const { splitName = null } = options;
	let aTime = null;
	let bTime = null;
	let forceOK = true;

	if (splitName == null) {
		aTime = a.totalTimeInMilliseconds;
		bTime = b.totalTimeInMilliseconds;
	} else {
		forceOK = false;
		a.splits?.forEach(function(split) {
			if (split.name === splitName) {
				aTime = split.elapsed;
			}
		});
		b.splits?.forEach(function(split) {
			if (split.name === splitName) {
				bTime = split.elapsed;
			}
		});
	}

	// Try to compare by time first
	const timeComparison = compareByTime(aTime, bTime, a, b, forceOK);
	if (timeComparison !== null) {
		return timeComparison;
	}

	// Fall back to split comparison
	const splitComparison = compareBySplits(a, b);
	if (splitComparison !== 0) {
		return splitComparison;
	}

	// Final fallback: sort by bib if both are set
	if (a.bib != null && b.bib != null) {
		return a.bib > b.bib ? 1 : -1;
	}
	// Last resort: sort by id
	return a.id > b.id ? 1 : -1;
}

/**
 * Sprint race sort function - simpler, no split fallback
 * @param {Object} a - Result object A
 * @param {Object} b - Result object B
 * @returns {number} Sort comparison result
 */
export function sprintSortFn(a, b) {
	const aTime = a.totalTimeInMilliseconds;
	const bTime = b.totalTimeInMilliseconds;

	// Both have times and OK status
	if (aTime != null && bTime != null && (a.status === "OK" && b.status === "OK")) {
		return aTime > bTime ? 1 : aTime < bTime ? -1 : (a.id > b.id ? 1 : -1);
	}
	// A has time and OK
	if (aTime != null && a.status === "OK") {
		return -1;
	}
	// B has time and OK
	if (bTime != null && b.status === "OK") {
		return 1;
	}
	// Final sort by id as fallback
	return a.id > b.id ? 1 : -1;
}

/**
 * Pursuit race sort function - includes pts3 time bonus
 * @param {Object} a - Result object A
 * @param {Object} b - Result object B
 * @returns {number} Sort comparison result
 */
export function pursuitSortFn(a, b) {
	let aTime = null;
	let bTime = null;
	const forceOK = true;

	if (a.totalTimeInMilliseconds) {
		aTime = a.totalTimeInMilliseconds + a.pts3 * 1000;
	}
	if (b.totalTimeInMilliseconds) {
		bTime = b.totalTimeInMilliseconds + b.pts3 * 1000;
	}

	if (a.pts3 === 0 || a.pts3 == null) {
		aTime = null;
	}
	if (b.pts3 === 0 || b.pts3 == null) {
		bTime = null;
	}

	// Try to compare by time first
	const timeComparison = compareByTime(aTime, bTime, a, b, forceOK);
	if (timeComparison !== null) {
		return timeComparison;
	}

	// Fall back to split comparison with time bonuses
	const timeBonusA = (a.pts3 || 0) * 1000;
	const timeBonusB = (b.pts3 || 0) * 1000;

	const splitComparison = compareBySplits(a, b, timeBonusA, timeBonusB);
	if (splitComparison !== 0) {
		return splitComparison;
	}

	// Sort by start time
	return a.pts1 > b.pts1 ? 1 : -1;
}

/**
 * Enduro sort function - sorts by place
 * @param {Object} a - Result object A
 * @param {Object} b - Result object B
 * @returns {number} Sort comparison result
 */
export function enduroSortFn(a, b) {
	return a.place < b.place ? -1 : a.place > b.place ? 1 : 0;
}

/**
 * Calculate biathlon loop statistics for a result
 * @param {Array} biathlon - Array of biathlon loop objects
 * @returns {Object} Object with numLoops and lastLoopIndex
 */
export function calculateBiathlonStats(biathlon) {
	let numLoops = 0;
	let lastLoopIndex = 0;

	if (biathlon != undefined) {
		biathlon.forEach(function(loop, index) {
			if (loop.cumulativeTime && loop.cumulativeTime.timeInMilliseconds != null) {
				numLoops++;
				if (index > lastLoopIndex) {
					lastLoopIndex = index;
				}
			}
		});
	}

	return { numLoops, lastLoopIndex };
}

/**
 * Compare two results by their biathlon loops when no total time is available
 * @param {Object} a - Result object A
 * @param {Object} b - Result object B
 * @returns {number} Sort comparison result
 */
export function compareByBiathlonLoops(a, b) {
	const aBiathlon = a.biathlon;
	const bBiathlon = b.biathlon;

	const aStats = calculateBiathlonStats(aBiathlon);
	const bStats = calculateBiathlonStats(bBiathlon);

	// If neither has loops, return 0 (caller should handle fallback)
	if (aStats.numLoops === 0 && bStats.numLoops === 0) {
		return 0;
	}

	// More loops = further in race = higher ranking
	if (aStats.numLoops !== bStats.numLoops) {
		return aStats.numLoops > bStats.numLoops ? -1 : 1;
	}

	// Same number of loops - compare cumulative times at last loop
	const aTime = aBiathlon[aStats.lastLoopIndex].cumulativeTime.timeInMilliseconds;
	const bTime = bBiathlon[bStats.lastLoopIndex].cumulativeTime.timeInMilliseconds;

	return aTime > bTime ? 1 : aTime < bTime ? -1 : 0;
}

/**
 * Biathlon race sort function
 * @param {Object} a - Result object A
 * @param {Object} b - Result object B
 * @returns {number} Sort comparison result
 */
export function biathlonSortFn(a, b) {
	const aTime = a.totalTimeInMilliseconds;
	const bTime = b.totalTimeInMilliseconds;
	const forceOK = true;

	// Try to compare by total time first
	const timeComparison = compareByTime(aTime, bTime, a, b, forceOK);
	if (timeComparison !== null) {
		return timeComparison;
	}

	// Fall back to biathlon loop comparison
	const loopComparison = compareByBiathlonLoops(a, b);
	if (loopComparison !== 0) {
		return loopComparison;
	}

	// Final fallback: sort by bib if both are set
	if (a.bib != null && b.bib != null) {
		return a.bib > b.bib ? 1 : -1;
	}
	// Last resort: sort by id
	return a.id > b.id ? 1 : -1;
}
